import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminSendAnnouncement } from "../lib/api";

type TargetType = "all" | "active" | "verified";

export default function AdminAnnouncements() {
  const navigate = useNavigate();
  
  const [message, setMessage] = useState("");
  const [targetType, setTargetType] = useState<TargetType>("all");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const MAX_LENGTH = 500;
  const MIN_LENGTH = 10;
  const characterCount = message.length;

  const getTargetDescription = (type: TargetType): string => {
    switch (type) {
      case "all":
        return "Tüm kullanıcılar";
      case "active":
        return "Son 7 günde aktif kullanıcılar";
      case "verified":
        return "Email doğrulamış kullanıcılar";
      default:
        return "Kullanıcılar";
    }
  };

  const handleSubmit = async () => {
    // Validation
    setError(null);
    setSuccess(null);

    if (!message.trim()) {
      setError("Lütfen bir mesaj yazın");
      return;
    }

    if (message.length < MIN_LENGTH) {
      setError(`Mesaj en az ${MIN_LENGTH} karakter olmalıdır`);
      return;
    }

    if (message.length > MAX_LENGTH) {
      setError(`Mesaj en fazla ${MAX_LENGTH} karakter olabilir`);
      return;
    }

    // Confirm dialog
    const confirmed = window.confirm(
      `Bu duyuruyu "${getTargetDescription(targetType)}" göndermek istediğinizden emin misiniz?\n\n` +
      `Mesaj önizleme:\n"${message.substring(0, 100)}${message.length > 100 ? "..." : ""}"\n\n` +
      `Bu işlem geri alınamaz.`
    );

    if (!confirmed) {
      return;
    }

    // API call
    setSubmitting(true);
    setError(null);

    try {
      const result = await adminSendAnnouncement({
        message: message.trim(),
        targetType,
      });

      const sentCount = result.sent || 0;

      setSuccess(
        `✅ Duyuru başarıyla gönderildi!\n` +
        `📊 Hedef: ${getTargetDescription(targetType)}\n` +
        `📤 Gönderilen: ${sentCount} kullanıcı`
      );

      // Clear form
      setMessage("");
      setTargetType("all");

    } catch (err: any) {
      console.error("Announcement send error:", err);
      setError(`⚠️ Bir hata oluştu: ${err.message || "Bilinmeyen hata"}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg)",
        color: "var(--text)",
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 24,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: 32,
                fontWeight: 900,
                margin: 0,
                marginBottom: 8,
              }}
            >
              📢 Toplu Duyuru Gönder
            </h1>
            <p
              style={{
                fontSize: 14,
                color: "var(--muted)",
                margin: 0,
              }}
            >
              Seçili kullanıcı grubuna sistem duyurusu gönderin
            </p>
          </div>
          <button
            onClick={() => navigate("/settings")}
            style={{
              padding: "8px 16px",
              background: "transparent",
              border: "1px solid var(--muted)",
              borderRadius: 8,
              color: "var(--text)",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            ← Geri
          </button>
        </div>

        {/* Info Box */}
        <div
          style={{
            background: "rgba(59, 130, 246, 0.1)",
            border: "1px solid rgba(59, 130, 246, 0.3)",
            borderRadius: 12,
            padding: 16,
            marginBottom: 24,
            display: "flex",
            gap: 12,
          }}
        >
          <span style={{ fontSize: 24 }}>📢</span>
          <div style={{ flex: 1 }}>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                lineHeight: 1.6,
                color: "#93c5fd",
              }}
            >
              Toplu duyuru sistemi ile seçili kullanıcılara bildirim
              gönderebilirsiniz. Duyurular kullanıcıların bildirimler
              sayfasında görünecektir.
            </p>
          </div>
        </div>

        {/* Main Form Card */}
        <div
          style={{
            background: "var(--bg2)",
            border: "1px solid var(--muted)",
            borderRadius: 16,
            padding: 24,
          }}
        >
          {/* Target Type Selection */}
          <div style={{ marginBottom: 24 }}>
            <label
              style={{
                display: "block",
                fontSize: 16,
                fontWeight: 700,
                marginBottom: 12,
                color: "var(--text)",
              }}
            >
              Hedef Kitle
            </label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 12,
              }}
            >
              {[
                {
                  type: "all" as const,
                  icon: "👥",
                  label: "Tüm Kullanıcılar",
                  desc: "Tüm kayıtlı kullanıcılar",
                },
                {
                  type: "active" as const,
                  icon: "🔥",
                  label: "Aktif Kullanıcılar",
                  desc: "Son 7 günde aktif olanlar",
                },
                {
                  type: "verified" as const,
                  icon: "✅",
                  label: "Onaylı Kullanıcılar",
                  desc: "Email onaylı kullanıcılar",
                },
              ].map((option) => (
                <button
                  key={option.type}
                  onClick={() => setTargetType(option.type)}
                  disabled={submitting}
                  style={{
                    background:
                      targetType === option.type
                        ? "var(--primary)"
                        : "var(--bg)",
                    border:
                      targetType === option.type
                        ? "2px solid var(--primary)"
                        : "1px solid var(--muted)",
                    borderRadius: 12,
                    padding: 16,
                    cursor: submitting ? "not-allowed" : "pointer",
                    textAlign: "left",
                    transition: "all 0.2s",
                    opacity: submitting ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (targetType !== option.type && !submitting) {
                      e.currentTarget.style.borderColor = "var(--primary)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (targetType !== option.type) {
                      e.currentTarget.style.borderColor = "var(--muted)";
                    }
                  }}
                >
                  <div
                    style={{
                      fontSize: 28,
                      marginBottom: 8,
                    }}
                  >
                    {option.icon}
                  </div>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      marginBottom: 4,
                      color:
                        targetType === option.type ? "#fff" : "var(--text)",
                    }}
                  >
                    {option.label}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color:
                        targetType === option.type
                          ? "rgba(255,255,255,0.8)"
                          : "var(--muted)",
                    }}
                  >
                    {option.desc}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Message Input */}
          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <label
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "var(--text)",
                }}
              >
                Duyuru Mesajı
              </label>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color:
                    characterCount > MAX_LENGTH
                      ? "#ef4444"
                      : characterCount < MIN_LENGTH
                      ? "#fbbf24"
                      : "#10b981",
                }}
              >
                {characterCount}/{MAX_LENGTH}
              </span>
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={submitting}
              placeholder="Duyuru mesajınızı buraya yazın..."
              maxLength={MAX_LENGTH}
              style={{
                width: "100%",
                minHeight: 180,
                background: "var(--bg)",
                border: "1px solid var(--muted)",
                borderRadius: 12,
                padding: 16,
                fontSize: 15,
                color: "var(--text)",
                fontFamily: "inherit",
                resize: "vertical",
                opacity: submitting ? 0.6 : 1,
                cursor: submitting ? "not-allowed" : "text",
              }}
            />
            {message.length > 0 && message.length < MIN_LENGTH && (
              <p
                style={{
                  fontSize: 13,
                  color: "#fbbf24",
                  margin: "8px 0 0 0",
                }}
              >
                ⚠️ En az {MIN_LENGTH} karakter gerekli ({MIN_LENGTH - message.length} karakter daha)
              </p>
            )}
          </div>

          {/* Preview */}
          {message.trim().length >= MIN_LENGTH && (
            <div style={{ marginBottom: 24 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 16,
                  fontWeight: 700,
                  marginBottom: 12,
                  color: "var(--text)",
                }}
              >
                Önizleme
              </label>
              <div
                style={{
                  background: "var(--bg)",
                  border: "1px solid var(--muted)",
                  borderRadius: 12,
                  padding: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    marginBottom: 12,
                  }}
                >
                  <span style={{ fontSize: 32 }}>📢</span>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        marginBottom: 4,
                      }}
                    >
                      Sistem Duyurusu
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "var(--muted)",
                      }}
                    >
                      Şimdi
                    </div>
                  </div>
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 15,
                    lineHeight: 1.6,
                    color: "var(--text)",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {message}
                </p>
              </div>
            </div>
          )}

          {/* Warning Box */}
          <div
            style={{
              background: "rgba(251, 191, 36, 0.1)",
              border: "1px solid rgba(251, 191, 36, 0.3)",
              borderRadius: 12,
              padding: 16,
              marginBottom: 24,
              display: "flex",
              gap: 12,
            }}
          >
            <span style={{ fontSize: 20 }}>⚠️</span>
            <p
              style={{
                margin: 0,
                fontSize: 13,
                lineHeight: 1.5,
                color: "#fbbf24",
              }}
            >
              Bu işlem geri alınamaz. Duyuru gönderildikten sonra tüm seçili
              kullanıcılar bildirimi alacaktır. Push notification ve bildirim
              sistemi aracılığıyla iletim yapılacaktır.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div
              style={{
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                borderRadius: 12,
                padding: 16,
                marginBottom: 24,
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  color: "#ef4444",
                  whiteSpace: "pre-wrap",
                }}
              >
                {error}
              </p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div
              style={{
                background: "rgba(16, 185, 129, 0.1)",
                border: "1px solid rgba(16, 185, 129, 0.3)",
                borderRadius: 12,
                padding: 16,
                marginBottom: 24,
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  color: "#10b981",
                  whiteSpace: "pre-wrap",
                }}
              >
                {success}
              </p>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={
              submitting ||
              !message.trim() ||
              message.length < MIN_LENGTH ||
              message.length > MAX_LENGTH
            }
            style={{
              width: "100%",
              background:
                submitting ||
                !message.trim() ||
                message.length < MIN_LENGTH ||
                message.length > MAX_LENGTH
                  ? "#4b5563"
                  : "var(--primary)",
              border: "none",
              borderRadius: 12,
              padding: "16px 24px",
              fontSize: 17,
              fontWeight: 700,
              color: "#fff",
              cursor:
                submitting ||
                !message.trim() ||
                message.length < MIN_LENGTH ||
                message.length > MAX_LENGTH
                  ? "not-allowed"
                  : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              transition: "all 0.2s",
            }}
          >
            {submitting ? (
              <>
                <div
                  style={{
                    width: 20,
                    height: 20,
                    border: "3px solid rgba(255,255,255,0.3)",
                    borderTop: "3px solid #fff",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
                <span>Gönderiliyor...</span>
              </>
            ) : (
              <>
                <span style={{ fontSize: 20 }}>📨</span>
                <span>Duyuruyu Gönder</span>
              </>
            )}
          </button>
        </div>

        {/* Info Footer */}
        <div
          style={{
            marginTop: 24,
            padding: 16,
            background: "rgba(107, 114, 128, 0.1)",
            border: "1px solid rgba(107, 114, 128, 0.2)",
            borderRadius: 12,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 13,
              color: "var(--muted)",
              lineHeight: 1.6,
            }}
          >
            💡 <strong>Bilgi:</strong> Gönderilen duyurular kullanıcıların
            bildirimler sayfasında gösterilir. İleride bir "Duyuru Geçmişi"
            sayfası eklenebilir.
          </p>
        </div>
      </div>

      {/* Spinner Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
